import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export type LanguageCode = 'en' | 'ro';

type Translations = Record<string, Record<LanguageCode, string>>;

const translations: Translations = {
  // Navigation
  'nav.work': { en: 'work', ro: 'lucrări' },
  'nav.about': { en: 'about', ro: 'despre' },
  'nav.music': { en: 'music', ro: 'muzică' },
  'nav.contact': { en: 'contact', ro: 'contact' },
  'nav.back': { en: '← BACK', ro: '← ÎNAPOI' },

  // Hero
  'hero.eyebrow': { en: '[ PRODUCER // ARTIST // RO ]', ro: '[ PRODUCĂTOR // ARTIST // RO ]' },
  'hero.desc': {
    en: 'Pioneering rage trap in Romania.\nInspired by Trippie, Yeat, Azteca.\nNo filters. No compromise.',
    ro: 'Pionier al rage trap-ului în România.\nInspirație: Trippie, Yeat, Azteca.\nFără filtre. Fără compromis.',
  },
  'hero.scroll': { en: '↘ SCROLL TO ENTER', ro: '↘ DERULEAZĂ' },

  // Section labels
  'section.selectedWork': { en: 'SELECTED WORK', ro: 'LUCRĂRI SELECTATE' },
  'section.about': { en: 'ABOUT', ro: 'DESPRE' },
  'section.nowPlaying': { en: 'NOW PLAYING', ro: 'ACUM REDĂ' },
  'section.credits': { en: 'DISCOGRAPHY // CREDITS', ro: 'DISCOGRAFIE // CREDITE' },
  'section.getInTouch': { en: 'GET IN TOUCH', ro: 'CONTACTEAZĂ-MĂ' },

  // About
  'about.heading1': { en: 'The', ro: 'Sunetul' },
  'about.heading2': { en: 'Sound', ro: 'Din' },
  'about.heading3': { en: 'Behind', ro: 'Spatele' },
  'about.heading4': { en: 'It.', ro: 'Scenei.' },
  'about.bio1': {
    en: 'andrxw is an artist and producer from Romania. After his sampled track "Old Times" took off, he decided to take music seriously and fully embrace life as an artist.',
    ro: 'andrxw este un artist și producător din România. După ce piesa sa samplată "Old Times" a prins tracțiune, a decis să ia muzica în serios și să îmbrățișeze pe deplin viața de artist.',
  },
  'about.bio2': {
    en: 'Drawn to the worlds of Trippie Redd, Yeat, and Azteca, he started producing in that vein and fell deep into it — then kept going, pushing further into Rage Music. Where most of the genre performs wealth, andrxw writes from the other side of it — self-produced, raw, and building something Romania hasn\'t heard before.',
    ro: 'Atras de lumile lui Trippie Redd, Yeat și Azteca, a început să producă în stilul lor și s-a cufundat adânc — apoi a continuat, împingând mai departe în Rage Music. Acolo unde majoritatea genului performează bogăția, andrxw scrie din cealaltă parte — self-produced, raw, și construind ceva ce România nu a mai auzit.',
  },

  // Contact
  'contact.heading1': { en: "Let's", ro: 'Hai să' },
  'contact.heading2': { en: 'Build.', ro: 'Creăm.' },
  'contact.cta': { en: 'BOOK SESSION ↘', ro: 'REZERVĂ SESIUNE ↘' },

  // Credits
  'credits.discography': { en: 'DISCOGRAPHY ↘', ro: 'DISCOGRAFIE ↘' },
  'credits.workedWith': { en: 'WORKED WITH', ro: 'A LUCRAT CU' },
  'credits.roles': { en: 'ROLES', ro: 'ROLURI' },
  'credits.platforms': { en: 'PLATFORMS ↘', ro: 'PLATFORME ↘' },

  // Player
  'player.prev': { en: '⏮ PREV', ro: '⏮ ÎNAPOI' },
  'player.play': { en: '▶ PLAY', ro: '▶ REDĂ' },
  'player.pause': { en: '⏸ PAUSE', ro: '⏸ PAUZĂ' },
  'player.next': { en: 'NEXT ⏭', ro: 'URMĂTOR ⏭' },

  // Footer
  'footer.rights': { en: 'ALL RIGHTS RESERVED', ro: 'TOATE DREPTURILE REZERVATE' },
};

interface I18nContextValue {
  lang: LanguageCode;
  setLang: (lang: LanguageCode) => void;
  t: (key: string) => string;
}

const I18nContext = createContext<I18nContextValue | undefined>(undefined);

const storageKey = 'andrxw-lang';

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [lang, setLangState] = useState<LanguageCode>('en');

  useEffect(() => {
    const saved = localStorage.getItem(storageKey) as LanguageCode | null;
    if (saved === 'en' || saved === 'ro') {
      setLangState(saved);
    } else {
      const browser = navigator.language?.toLowerCase();
      setLangState(browser.startsWith('ro') ? 'ro' : 'en');
    }
  }, []);

  const setLang = useCallback((l: LanguageCode) => {
    setLangState(l);
    localStorage.setItem(storageKey, l);
  }, []);

  const t = useCallback((key: string) => {
    const entry = translations[key];
    if (!entry) return key;
    return entry[lang] ?? entry.en;
  }, [lang]);

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = (): I18nContextValue => {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within I18nProvider');
  return ctx;
};
